import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { useCustomStore } from '../../../../hooks';
import {
  AddMotorClubUnitMembershipRequest,
  MotorClubEligibleUnit,
  MotorClubMembershipDriver,
  MotorClubRoadsideUnit,
} from '../../../../store/api/motorClubMembership.api';
import AddMembershipUnitModal, {
  AddMembershipUnitPayload,
  ExistingDriverOption,
  ExistingUnitOption,
} from './AddMembershipUnitModal';

const PAGE_LIMIT = 20;
const UNITS_SCROLL_CONTAINER_ID = 'motorclub-roadside-units-scroll';

const getUnitStatusColors = (status: string): { bg: string; color: string } => {
  const normalized = status?.toLowerCase?.() || '';
  if (normalized.includes('active')) {
    return {
      bg: '#E7F5EC',
      color: '#2F8F4E',
    };
  }
  if (normalized.includes('service')) {
    return {
      bg: '#E7EEFB',
      color: '#2358A8',
    };
  }
  if (normalized.includes('out')) {
    return {
      bg: '#FBE6EA',
      color: '#B32A3D',
    };
  }

  return {
    bg: '#EEEEF0',
    color: '#66666F',
  };
};

const getInitials = (text: string): string =>
  text
    .split(/[-\s]+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .join('')
    .slice(0, 2);

const MembershipRoadUnitsGrid = ({
  showDriverView,
}: {
  showDriverView?: boolean;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { authStore, motorClubMembershipStore } = useCustomStore();
  const [activeTab, setActiveTab] = React.useState<'active' | 'terminated'>(
    'active',
  );
  const [isAddUnitModalOpen, setIsAddUnitModalOpen] = React.useState(false);
  const [isEligibleDriversLoading, setIsEligibleDriversLoading] =
    React.useState(false);
  const [isEligibleUnitsLoading, setIsEligibleUnitsLoading] =
    React.useState(false);
  const [eligibleDriverOptions, setEligibleDriverOptions] = React.useState<
    ExistingDriverOption[]
  >([]);
  const [eligibleUnitOptions, setEligibleUnitOptions] = React.useState<
    ExistingUnitOption[]
  >([]);
  const [driverMembershipByDriverId, setDriverMembershipByDriverId] =
    React.useState<Record<string, string>>({});
  const [expandedRows, setExpandedRows] = React.useState<
    Record<string, boolean>
  >({});
  const [terminateTarget, setTerminateTarget] =
    React.useState<MotorClubRoadsideUnit | null>(null);
  const [isTerminationIneligible, setIsTerminationIneligible] =
    React.useState(false);
  const [isTerminatingUnit, setIsTerminatingUnit] = React.useState(false);

  const roadsideUnits = motorClubMembershipStore.RoadsideUnits;
  const membershipDetails = motorClubMembershipStore.MembershipDetails;

  const loadRoadsideUnits = React.useCallback(
    async (append = false) => {
      const responseOrErr = await motorClubMembershipStore.getRoadsideUnits({
        append,
        limit: PAGE_LIMIT,
        terminated: activeTab === 'terminated',
      });

      if (responseOrErr?.isErr?.()) {
        enqueueSnackbar(responseOrErr.error.message, {
          variant: 'error',
        });
      }
    },
    [activeTab, enqueueSnackbar, motorClubMembershipStore],
  );

  React.useEffect(() => {
    loadRoadsideUnits(false);
  }, [loadRoadsideUnits]);

  const loadEligibleUnits = React.useCallback(async () => {
    setIsEligibleUnitsLoading(true);
    const responseOrErr =
      await motorClubMembershipStore.rootStore.apiStore.motorClubMembershipApiStore.getEligibleUnits();
    setIsEligibleUnitsLoading(false);

    if (responseOrErr.isErr()) {
      enqueueSnackbar(responseOrErr.error.message, {
        variant: 'error',
      });
      return;
    }

    const options = (responseOrErr.value.items || []).map(
      (item: MotorClubEligibleUnit) => ({
        unitId: item.unitId || '',
        unitLabel: item.unitNumber || '',
        vinNumber: item.vinNumber || '',
      }),
    );

    setEligibleUnitOptions(options);
  }, [
    enqueueSnackbar,
    motorClubMembershipStore.rootStore.apiStore.motorClubMembershipApiStore,
  ]);

  const loadEligibleDrivers = React.useCallback(async () => {
    setIsEligibleDriversLoading(true);

    const [responseOrErr, membershipsOrErr] = await Promise.all([
      motorClubMembershipStore.rootStore.apiStore.motorClubMembershipApiStore.getEligibleDrivers(
        true,
      ),
      motorClubMembershipStore.rootStore.apiStore.motorClubMembershipApiStore.getMemberships(
        { limit: 200 },
      ),
    ]);
    setIsEligibleDriversLoading(false);

    if (responseOrErr.isErr()) {
      enqueueSnackbar(responseOrErr.error.message, {
        variant: 'error',
      });
      return;
    }

    if (membershipsOrErr.isErr()) {
      enqueueSnackbar(membershipsOrErr.error.message, {
        variant: 'error',
      });
      return;
    }

    const membershipMap = (membershipsOrErr.value.items || []).reduce(
      (acc: Record<string, string>, membership: MotorClubMembershipDriver) => {
        if (membership.driverId && membership.memberId) {
          acc[membership.driverId] = membership.memberId;
        }

        return acc;
      },
      {},
    );
    setDriverMembershipByDriverId(membershipMap);

    const options = responseOrErr.value?.items || [];

    setEligibleDriverOptions(options);
  }, [
    enqueueSnackbar,
    motorClubMembershipStore.rootStore.apiStore.motorClubMembershipApiStore,
  ]);

  React.useEffect(() => {
    if (!isAddUnitModalOpen) {
      return;
    }

    loadEligibleUnits();
    if (!showDriverView) {
      loadEligibleDrivers();
    }
  }, [
    isAddUnitModalOpen,
    showDriverView,
    loadEligibleDrivers,
    loadEligibleUnits,
  ]);

  const toggleRow = (unitNumber: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [unitNumber]: !prev[unitNumber],
    }));
  };

  const handleAddUnit = async (
    payload: AddMembershipUnitPayload,
  ): Promise<boolean> => {
    const linkedDriverMembershipId = payload.linkedDriverId
      ? driverMembershipByDriverId[payload.linkedDriverId]
      : undefined;

    const requestPayload: AddMotorClubUnitMembershipRequest =
      payload.source === 'existing'
        ? {
            existingUnitId: payload.existingUnitId,
            linkedDriverId: payload.linkedDriverId,
            linkedDriverMembershipId,
          }
        : {
            linkedDriverId: payload.linkedDriverId,
            linkedDriverMembershipId,
            unit: payload.unit,
          };

    const responseOrErr = await motorClubMembershipStore.addMembershipUnit(
      requestPayload,
    );

    if (responseOrErr.isErr()) {
      enqueueSnackbar(responseOrErr.error.message, {
        variant: 'error',
      });

      return false;
    }

    enqueueSnackbar(`Unit added successfully: ${payload.unitLabel}`, {
      variant: 'success',
    });

    const [, membershipDriversOrErr] = await Promise.all([
      loadRoadsideUnits(false),
      motorClubMembershipStore.getMembershipDrivers({
        append: false,
        limit: PAGE_LIMIT,
      }),
    ]);

    if (membershipDriversOrErr?.isErr?.()) {
      enqueueSnackbar(membershipDriversOrErr.error.message, {
        variant: 'error',
      });
    }

    await loadEligibleUnits();

    return true;
  };

  const handleTerminateUnit = async () => {
    if (!terminateTarget) {
      return;
    }

    if (!terminateTarget.memberId) {
      enqueueSnackbar('Unable to terminate this unit membership.', {
        variant: 'error',
      });
      return;
    }

    setIsTerminatingUnit(true);
    const terminateOrErr = await motorClubMembershipStore.terminateMembership({
      id: terminateTarget.memberId,
      membershipType: 2,
    });
    setIsTerminatingUnit(false);

    if (terminateOrErr?.isErr?.()) {
      enqueueSnackbar(terminateOrErr.error.message, {
        variant: 'error',
      });
      return;
    }

    await Promise.all([
      loadRoadsideUnits(false),
      motorClubMembershipStore.getMembershipDrivers({
        append: false,
        limit: PAGE_LIMIT,
      }),
      loadEligibleUnits(),
    ]);

    if (
      terminateOrErr.value?.notifiedDriverIds?.length &&
      !terminateOrErr.value?.notificationSent
    ) {
      enqueueSnackbar(
        'Unit terminated, but notifications could not be sent to associated drivers.',
        {
          variant: 'warning',
        },
      );
    } else {
      enqueueSnackbar('Unit membership terminated successfully', {
        variant: 'success',
      });
    }

    setTerminateTarget(null);
  };

  const requestUnitTermination = (unit: MotorClubRoadsideUnit) => {
    const currentDriverId = authStore.getTokenDetail?.id || '';
    const activatingDriverId = unit.driverMembership?.driverId || '';

    if (showDriverView && currentDriverId !== activatingDriverId) {
      setIsTerminationIneligible(true);
      return;
    }

    setTerminateTarget(unit);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #E2E2E6',
        borderRadius: '10px',
        overflow: 'hidden',
      }}>
      <Box
        sx={{
          alignItems: 'center',
          borderBottom: '1px solid #EEEEF0',
          borderTop: '1px solid #EEEEF0',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          justifyContent: 'space-between',
          padding: '18px 22px',
        }}>
        <Box
          sx={{
            alignItems: 'flex-start',
            display: 'flex',
            flex: '1 1 0',
            flexDirection: 'column',
            gap: '2px',
            justifyContent: 'flex-start',
            minWidth: 0,
          }}>
          <Typography
            sx={{
              color: '#1A1A1A',
              fontFamily: 'FiraSans-SemiBold',
              fontSize: '15.5px',
              lineHeight: '22px',
            }}>
            Roadside Units
          </Typography>
          {showDriverView ? null : (
            <Typography
              sx={{
                ...spotsSummarySx,
                display: 'block',
                margin: 0,
              }}>
              <Box component="span" sx={spotsSummaryStrongSx}>
                {membershipDetails?.activeUnits ?? 0} of{' '}
                {membershipDetails?.totalPurchasedUnits ?? 0}
              </Box>{' '}
              used -{' '}
              <Box component="span" sx={spotsSummaryStrongSx}>
                {membershipDetails?.vacantUnits ?? 0} available
              </Box>
            </Typography>
          )}
        </Box>

        <Tabs
          onChange={(_event, value) => setActiveTab(value)}
          sx={{
            '& .Mui-selected': {
              color: '#C9A200 !important',
            },
            '& .MuiTab-root': {
              fontFamily: 'FiraSans-SemiBold',
              fontSize: '12px',
              minHeight: '32px',
              padding: '6px 12px',
              textTransform: 'none',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#C9A200',
            },
            flex: '0 0 auto',
            minHeight: '32px',
          }}
          value={activeTab}>
          <Tab label="Active" value="active" />
          <Tab label="Terminated" value="terminated" />
        </Tabs>

        <Box
          sx={{ display: 'flex', flex: '1 1 0', justifyContent: 'flex-end' }}>
          <Button
            onClick={() => {
              setIsAddUnitModalOpen(true);
            }}
            sx={{
              '&:hover': {
                backgroundColor: '#B69300',
              },
              backgroundColor: '#C9A200',
              borderRadius: '8px',
              color: '#1A1A1A',
              fontFamily: 'FiraSans-SemiBold',
              fontSize: '12.5px',
              minWidth: '112px',
              padding: '9px 15px',
              textTransform: 'none',
            }}>
            + Add Unit
          </Button>
        </Box>
      </Box>

      <AddMembershipUnitModal
        existingDrivers={eligibleDriverOptions}
        existingUnits={eligibleUnitOptions}
        showDriverView={showDriverView}
        isLoadingExistingDrivers={isEligibleDriversLoading}
        isLoadingExistingUnits={isEligibleUnitsLoading}
        onClose={() => setIsAddUnitModalOpen(false)}
        onSubmit={handleAddUnit}
        open={isAddUnitModalOpen}
      />

      <Box
        sx={{
          alignItems: 'center',
          borderBottom: '1px solid #EEEEF0',
          display: 'flex',
          flexWrap: { md: 'nowrap', xs: 'wrap' },
          gap: '10px',
          padding: '12px 22px',
        }}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: { md: '0 0 25%', xs: '1 1 100%' },
            minWidth: 0,
          }}>
          <Box sx={{ marginRight: '8px', width: 26 }} />
          <Box sx={{ marginRight: '6px', width: 20 }} />
          <Typography
            sx={{
              color: '#66666F',
              fontFamily: 'FiraSans-Bold',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '.4px',
              lineHeight: '16px',
              textTransform: 'uppercase',
            }}>
            Unit #
          </Typography>
        </Box>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: { md: '0 0 25%', xs: '1 1 100%' },
            minWidth: 0,
          }}>
          <Typography
            sx={{
              color: '#66666F',
              fontFamily: 'FiraSans-Bold',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '.4px',
              lineHeight: '16px',
              textTransform: 'uppercase',
            }}>
            VIN
          </Typography>
        </Box>
        {activeTab === 'terminated' ? (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flex: { md: '0 0 25%', xs: '1 1 100%' },
              minWidth: 0,
            }}>
            <Typography
              sx={{
                color: '#66666F',
                fontFamily: 'FiraSans-Bold',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '.4px',
                lineHeight: '16px',
                textTransform: 'uppercase',
              }}>
              Cancelled Date
            </Typography>
          </Box>
        ) : null}
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'flex-end',
            paddingRight: activeTab === 'active' ? '110px' : '22px',
            width: '100%',
          }}>
          <Typography
            sx={{
              color: '#66666F',
              fontFamily: 'FiraSans-Bold',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '.4px',
              lineHeight: '16px',
              textAlign: 'right',
              textTransform: 'uppercase',
            }}>
            Status
          </Typography>
        </Box>
      </Box>

      <Box
        id={UNITS_SCROLL_CONTAINER_ID}
        sx={{
          maxHeight: '440px',
          overflowY: 'auto',
        }}>
        {motorClubMembershipStore.isRoadsideUnitsLoading &&
        !roadsideUnits.length ? (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              minHeight: '140px',
            }}>
            <CircularProgress size={26} sx={{ color: '#DEC330' }} />
          </Box>
        ) : null}

        {!motorClubMembershipStore.isRoadsideUnitsLoading &&
        !roadsideUnits.length ? (
          <Box sx={{ padding: '18px 22px' }}>
            <Typography
              sx={{
                color: '#66666F',
                fontFamily: 'FiraSans-Regular',
                fontSize: '12.5px',
              }}>
              No roadside units found.
            </Typography>
          </Box>
        ) : null}

        {roadsideUnits.length ? (
          <InfiniteScroll
            dataLength={roadsideUnits.length}
            hasMore={motorClubMembershipStore.RoadsideUnitsHasMore}
            loader={
              <Box sx={{ py: 1.5, textAlign: 'center' }}>
                <CircularProgress size={22} sx={{ color: '#DEC330' }} />
              </Box>
            }
            next={() => {
              loadRoadsideUnits(true);
            }}
            scrollableTarget={UNITS_SCROLL_CONTAINER_ID}
            style={{ overflow: 'unset' }}>
            {roadsideUnits.map((row, index) => {
              const statusColor = getUnitStatusColors(row.status);
              const isExpanded = !!expandedRows[row.unitNumber];
              const firstDriver = row.driverMembership?.driverName || null;

              return (
                <Box
                  key={`${row.unitNumber}-${index}`}
                  sx={{
                    borderBottom:
                      index === roadsideUnits.length - 1
                        ? 'none'
                        : '1px solid #EEEEF0',
                  }}>
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      flexWrap: { md: 'nowrap', xs: 'wrap' },
                      gap: { md: 0, xs: '10px' },
                      padding: '13px 22px',
                    }}>
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flex: { md: '0 0 25%', xs: '1 1 100%' },
                        minWidth: 0,
                      }}>
                      <Box
                        sx={{
                          alignItems: 'center',
                          backgroundColor: '#EEEEF0',
                          borderRadius: '8px',
                          color: '#66666F',
                          display: 'flex',
                          height: 26,
                          justifyContent: 'center',
                          marginRight: '8px',
                          width: 26,
                        }}>
                        <LocalShippingOutlinedIcon sx={{ fontSize: 15 }} />
                      </Box>

                      <Button
                        onClick={() => toggleRow(row.unitNumber)}
                        sx={{
                          color: '#66666F',
                          marginRight: '6px',
                          minWidth: '20px',
                          padding: 0,
                        }}>
                        {isExpanded ? (
                          <KeyboardArrowUpIcon sx={{ fontSize: 17 }} />
                        ) : (
                          <KeyboardArrowDownIcon sx={{ fontSize: 17 }} />
                        )}
                      </Button>

                      <Typography
                        sx={{
                          color: '#2B2B2B',
                          fontFamily: 'FiraSans-SemiBold',
                          fontSize: '13.5px',
                        }}>
                        {row.unitNumber || '--'}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flex: { md: '0 0 25%', xs: '1 1 100%' },
                        minWidth: 0,
                      }}>
                      <Typography
                        sx={{
                          color: '#66666F',
                          fontFamily: 'FiraSans-Regular',
                          fontSize: '11.5px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                        {row.vinNumber || '--'}
                      </Typography>
                    </Box>

                    {activeTab === 'terminated' ? (
                      <Box
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          flex: { md: '0 0 25%', xs: '1 1 100%' },
                          minWidth: 0,
                        }}>
                        <Typography
                          sx={{
                            color: '#66666F',
                            fontFamily: 'FiraSans-Regular',
                            fontSize: '11.5px',
                          }}>
                          {row.cancelledDate || '--'}
                        </Typography>
                      </Box>
                    ) : null}

                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flex: {
                          md:
                            activeTab === 'terminated' ? '0 0 25%' : '0 0 50%',
                          xs: '1 1 100%',
                        },
                        gap: '10px',
                        justifyContent: { md: 'flex-end', xs: 'flex-start' },
                        minWidth: 0,
                      }}>
                      <Box
                        sx={{
                          alignItems: 'center',
                          backgroundColor: statusColor.bg,
                          borderRadius: '20px',
                          color: statusColor.color,
                          display: 'inline-flex',
                          fontFamily: 'FiraSans-SemiBold',
                          fontSize: '11.5px',
                          gap: '6px',
                          padding: '5px 10px',
                          whiteSpace: 'nowrap',
                        }}>
                        <span>{'●'}</span>
                        {row.status || 'Unknown'}
                      </Box>

                      {activeTab === 'terminated' ? null : (
                        <Button
                          onClick={() => {
                            requestUnitTermination(row);
                          }}
                          sx={{
                            '&:hover': {
                              backgroundColor: '#FBE6EA',
                              border: '1px solid #B32A3D',
                            },
                            border: '1px solid #B32A3D',
                            borderRadius: '8px',
                            color: '#B32A3D',
                            fontFamily: 'FiraSans-SemiBold',
                            fontSize: '11.5px',
                            minWidth: '68px',
                            padding: '6px 10px',
                            textTransform: 'none',
                          }}>
                          Terminate
                        </Button>
                      )}
                    </Box>
                  </Box>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box
                      sx={{
                        backgroundColor: '#FBFBFC',
                        borderTop: '1px solid #EEEEF0',
                        padding: '10px 22px 14px 42px',
                      }}>
                      {firstDriver ? (
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: '8px',
                          }}>
                          <Box
                            sx={{
                              alignItems: 'center',
                              backgroundColor: '#EEEEF0',
                              borderRadius: '50%',
                              color: '#66666F',
                              display: 'flex',
                              fontFamily: 'FiraSans-SemiBold',
                              fontSize: '10px',
                              height: 22,
                              justifyContent: 'center',
                              width: 22,
                            }}>
                            {getInitials(firstDriver)}
                          </Box>
                          <Typography
                            sx={{
                              color: '#2B2B2B',
                              fontFamily: 'FiraSans-Regular',
                              fontSize: '12px',
                            }}>
                            {firstDriver}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography
                          sx={{
                            color: '#9A9AA2',
                            fontFamily: 'FiraSans-Regular',
                            fontSize: '12px',
                          }}>
                          No driver currently assigned.
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </InfiniteScroll>
        ) : null}
      </Box>

      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => {
          if (!isTerminatingUnit) {
            setTerminateTarget(null);
          }
        }}
        open={!!terminateTarget}>
        <DialogTitle
          sx={{
            fontFamily: 'FiraSans-SemiBold',
          }}>
          Terminate Unit Membership
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: '#2B2B2B',
              fontFamily: 'FiraSans-Regular',
              fontSize: '13px',
            }}>
            The selected unit will be permanently removed from the Motor Club
            membership, the Roadside Assistance coverage for the unit will end,
            and any driver currently associated with the unit will no longer
            have Roadside Assistance coverage. Do you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '10px 20px 16px' }}>
          <Button
            disabled={isTerminatingUnit}
            onClick={() => setTerminateTarget(null)}
            sx={{
              borderColor: '#C9CDD4',
              color: '#66666F',
              fontFamily: 'FiraSans-SemiBold',
              minWidth: '74px',
              textTransform: 'none',
            }}
            variant="outlined">
            No
          </Button>
          <Button
            disabled={isTerminatingUnit}
            onClick={handleTerminateUnit}
            sx={{
              '&:hover': {
                backgroundColor: '#B69300',
              },
              backgroundColor: '#C9A200',
              color: '#1A1A1A',
              fontFamily: 'FiraSans-SemiBold',
              minWidth: '74px',
              textTransform: 'none',
            }}
            variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => setIsTerminationIneligible(false)}
        open={isTerminationIneligible}>
        <DialogTitle sx={{ fontFamily: 'FiraSans-SemiBold' }}>
          Unit Not Eligible for Termination
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: '#2B2B2B',
              fontFamily: 'FiraSans-Regular',
              fontSize: '13px',
            }}>
            The unit is not eligible for termination since it was enrolled by
            another driver.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '10px 20px 16px' }}>
          <Button
            onClick={() => setIsTerminationIneligible(false)}
            sx={{
              '&:hover': {
                backgroundColor: '#B69300',
              },
              backgroundColor: '#C9A200',
              color: '#1A1A1A',
              fontFamily: 'FiraSans-SemiBold',
              minWidth: '74px',
              textTransform: 'none',
            }}
            variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default observer(MembershipRoadUnitsGrid);

const spotsSummarySx = {
  color: '#374151',
  fontFamily: 'FiraSans-Regular',
  fontSize: '13px',
  lineHeight: '17px',
};

const spotsSummaryStrongSx = {
  fontFamily: 'FiraSans-Bold',
  fontWeight: 700,
};
